import * as React from "react";
import {
  dotToneClass,
  getDeploymentStatusMeta,
  getServiceStatusMeta,
  StatusTone,
} from "../../lib/statusMeta";
import { cn } from "../../lib/utils";
import { DeploymentStatus, ServiceStatus } from "../../types";

type StatusDotProps =
  | {
      kind: "deployment";
      status: DeploymentStatus | string;
      tone?: never;
      label?: string;
      size?: "sm" | "md";
      className?: string;
    }
  | {
      kind: "service";
      status: ServiceStatus | string;
      tone?: never;
      label?: string;
      size?: "sm" | "md";
      className?: string;
    }
  | {
      kind?: undefined;
      tone: StatusTone;
      pulse?: boolean;
      label?: string;
      size?: "sm" | "md";
      className?: string;
    };

const sizes = {
  sm: "h-2 w-2",
  md: "h-2.5 w-2.5",
} as const;

export function StatusDot(props: StatusDotProps) {
  const size = props.size ?? "md";

  let tone: StatusTone;
  let pulse = false;
  let label: string | undefined = props.label;

  if (props.kind === "deployment") {
    const meta = getDeploymentStatusMeta(props.status);
    tone = meta.tone;
    pulse = !!meta.pulse;
    label = label ?? meta.label;
  } else if (props.kind === "service") {
    const meta = getServiceStatusMeta(props.status);
    tone = meta.tone;
    pulse = !!meta.pulse;
    label = label ?? meta.label;
  } else {
    tone = props.tone;
    pulse = !!props.pulse;
  }

  return (
    <span
      className={cn("inline-flex items-center justify-center", props.className)}
      role="status"
      aria-label={label}
    >
      {pulse && (
        <span
          className={cn(
            "absolute inline-flex rounded-full opacity-60 animate-ping",
            sizes[size],
            dotToneClass(tone)
          )}
          aria-hidden="true"
        />
      )}
      <span
        className={cn(
          "relative inline-flex rounded-full",
          sizes[size],
          dotToneClass(tone)
        )}
        aria-hidden="true"
      />
    </span>
  );
}
